import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  BarChart3,
  FileSearch,
  FileText,
  LayoutDashboard,
  Upload,
  Layers,
  Database,
} from 'lucide-react'
import leapMark from '@/assets/favicon.png'
import { useAppStore } from '@/app/store'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/utils/cn'

type NavItem = {
  key: string
  name: string
  href: string
  icon: typeof LayoutDashboard
  hidden?: boolean
}

export function Sidebar() {
  const { t } = useTranslation('Sidebar')
  const { sidebarOpen: expanded, toggleSidebar } = useAppStore()
  const collapsed = !expanded
  const location = useLocation()
  const { user } = useAuthStore()

  const role = String(user?.pod_role || '')
  const isTransporter = role === 'POD Transporter'
  const isViewer = role === 'POD Viewer' || role.toLowerCase() === 'pod viewer'
  const perms = Array.isArray(user?.permissions) ? user.permissions : []
  const canUpload = perms.includes('pod.upload')
  const canBulkUpload = perms.includes('pod.bulk_upload')
  const canSyncApprovals = perms.includes('pod.sync_approvals')
  const canApprove = perms.includes('pod.approve')

  const navigation: NavItem[] = [
    {
      key: 'dashboard',
      name: 'Dashboard',
      href: isTransporter ? '/transporter/dashboard' : '/dashboard',
      icon: LayoutDashboard,
    },

    // Employee navigation
    {
      key: 'uploadPOD',
      name: 'Upload POD',
      href: '/uploadPOD',
      icon: Upload,
      hidden: isTransporter || (!canUpload && !isViewer),
    },
    {
      key: 'bulkPodUpload',
      name: 'Bulk POD (drive)',
      href: '/bulkPodUpload',
      icon: Layers,
      hidden: isTransporter || (!canBulkUpload && !isViewer),
    },
    {
      key: 'approvalsSync',
      name: 'Sync approvals',
      href: '/it-admin/approvals-sync',
      icon: Database,
      hidden: isTransporter || (!canSyncApprovals && !isViewer),
    },
    {
      key: 'pendingPOD',
      name: 'Pending POD',
      href: '/pendingPOD',
      icon: FileSearch,
      hidden: isTransporter || (!canApprove && !isViewer),
    },
    {
      key: 'documentJourney',
      name: 'Document Journey',
      href: '/documentJourney',
      icon: FileText,
      hidden: isTransporter,
    },
    {
      key: 'PODReports',
      name: 'Reports',
      href: '/PODReports',
      icon: BarChart3,
      hidden: isTransporter,
    },

    // Transporter navigation
    {
      key: 'pendingUploadTransporter',
      name: 'Pending POD for Upload',
      href: '/transporter/pending-upload',
      icon: Upload,
      hidden: !isTransporter,
    },
    {
      key: 'documentJourney',
      name: 'Document Journey',
      href: '/transporter/document-journey',
      icon: FileText,
      hidden: !isTransporter,
    },
    {
      key: 'pendingApproveTransporter',
      name: 'Pending approval',
      href: '/transporter/pending-approve',
      icon: FileSearch,
      hidden: !isTransporter,
    },
    {
      key: 'approvedTransporter',
      name: 'Transporter reports',
      href: '/transporter/approved',
      icon: BarChart3,
      hidden: !isTransporter,
    },
  ].filter((item) => !item.hidden)

  return (
    <aside
      className={cn(
        'flex h-full min-h-0 shrink-0 flex-col bg-[var(--sidebar)] text-[var(--sidebar-foreground)] transition-all duration-200',
        'border-e border-[var(--sidebar-border)]',
        expanded ? 'w-60' : 'w-16'
      )}
    >
      <div className="flex h-16 shrink-0 items-center justify-between border-b border-[var(--sidebar-border)] px-4">
        <div className={cn('font-semibold tracking-tight', collapsed && 'hidden')}>POD</div>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md p-1.5 hover:bg-[var(--sidebar-accent)] transition-colors text-[var(--sidebar-foreground)]"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <img src={leapMark} alt="" width={22} height={22} className="size-[22px] object-contain" />
        </button>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-2">
        {navigation.map((item) => {
          const Icon = item.icon
          const translatedName = t(`menu.${item.key}`, item.name)
          return (
            <NavLink
              key={item.key}
              to={item.href}
              end={item.href === '/dashboard'}
              title={collapsed ? translatedName : undefined}
              className={({ isActive }) => {
                const active =
                  isActive ||
                  (item.href === '/dashboard' &&
                    (location.pathname === '/' || location.pathname === '/dashboard'))
                return cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  'hover:bg-[var(--sidebar-accent)]',
                  active && 'bg-[var(--sidebar-accent)] text-[var(--sidebar-primary)]',
                  collapsed && 'justify-center px-2'
                )
              }}
            >
              <Icon className="size-4 shrink-0" strokeWidth={1.75} />
              <span className={cn(collapsed && 'hidden', 'truncate')}>{translatedName}</span>
            </NavLink>
          )
        })}
      </nav>
    </aside>
  )
}
